<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
// Vue
import { computed, inject, ref } from "vue"
import { useI18n } from "vue-i18n"

// Helpers
import {
    formatDate,
    downloadPDF,
    formatPerson,
    isCurrentUserHolderForExtend,
    plainAddressForExtend,
    titulaireToExtendParty,
} from "@core/helpers"

// Types
import {
    ConsentementEtat,
    InformationPCE,
    LineBlock,
    LineBlockBadge,
    LineBlockCommand,
} from "@types"
import { Services } from "@services"

// Constant
import { DDA_STATUS, PCE_DDA_STATUS, SOURCES_TYPES } from "@core/constants"

// Store
import { useSourceStore } from "@core/stores/source.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
const sourceStore = useSourceStore()
const authStore = useAuthStore()
const buildingStore = useBuildingStore()

// Components
import type {
    SourceExtendDialogDonePayload,
    SourceExtendDialogMeter,
} from "@core/components/source/meter/form/extend/SourceExtendDialog.vue"

// Service
import { useEventBus } from "@vueuse/core"
import {
    MESSAGES_UBORDEAUX,
    buildUBordeauxConsentBlock,
} from "./meter-ubordeaux.helper"
import { ToastMessageOptions, useToast } from "primevue"

const { $getPreviewConsent } = inject<Services>("services")!

const busTask = useEventBus("task")
const toast = useToast()

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            ubordeaux: MESSAGES_UBORDEAUX,
            state: "État",
            state_consent: {
                revoked: "Révoqué",
                outdated: "Obsolète",
                unsigned: "Consentement non signé",
                active: "Actif",
                error: "Erreur inconnue",
            },
            owner: {
                label: "Titulaire",
                on_behalf: "représentée par",
                unknown: "Non disponible",
            },
            homeys: {
                title: "Consentement Homeys",
                date_begin: "Date de signature et de début",
                date_end: "Date de fin",
            },
            pce: {
                grdf_access: "Droit d’accès GRDF",
                date_begin_access: "Date de début d'autorisation",
                date_end_access: "Date de fin d'autorisation",
                right_acess: ({
                    named,
                }: {
                    named: (key: string) => string
                }) => {
                    const status = {
                        [DDA_STATUS.ACTIVE]: "Actif",
                        [DDA_STATUS.RECHECK]:
                            "Actif (vérification par GRDF en cours)",
                        [DDA_STATUS.EMPTY]: "Non déclaré",
                        [DDA_STATUS.A_VALIDER]: "À valider",
                    }
                    return (
                        status?.[named("etat_droit_acces")] ??
                        named("etat_droit_acces") ??
                        "Non disponible"
                    )
                },
            },
            regaz: {
                api_access: "Accès aux données de consommation",
            },
            elecsavoie: {
                api_access: "Accès aux données de consommation",
            },
            elecstrasbourg: {
                api_access: "Accès aux données de consommation",
            },
            cpcu: {
                api_access: "Accès aux données de consommation via l'API CPCU",
                billing_access:
                    "Accès à l'espace client (facturation et contrat)",
            },
            dalkia: {
                api_access: "Accès aux données de consommation",
            },
            sigdu: {
                api_access: "Accès aux données de consommation",
            },

            date: ({ named }: { named: (key: string) => string }) =>
                `${named("date") ? formatDate(named("date")) : "Non disponible"}`,

            options: {
                revoke: "Révoquer",
                download: "Télécharger",
                extend: ({ named }: { named: (key: string) => string }) =>
                    named("isOwner") ? "Prolonger" : "Demander le prolongement",
                change_owner: "Changer de titulaire",
            },
            warning: {
                title_revoked: "Consentement révoqué",
                content_revoked:
                    "Vous avez révoqué l'accès à vos données. \nPour pouvoir continuer de profiter des services, veuillez renouveler votre consentement.",
                button_renew: "Renouveler l'accès",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    meterId: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// STATE
// ================================

// variable pour gérer l'ouvreture ou la fermeture de la modale d'édition
const modalMeterRevokeConsentToggle = ref<boolean>(false)

/** Dialog prolongement de consentement (remplace le postMessage legacy vers l’iframe). */
const modalExtendConsentVisible = ref(false)

/** Dialog changement de titulaire (remplace le postMessage legacy vers l’iframe). */
const modalChangeOwnerVisible = ref(false)

// ================================
// COMPUTED
// ================================

/**
 * Récupère la source par son ID
 */
const getSourceById = computed(() => {
    return sourceStore.getSourceById(props.meterId)
})

/**
 * Récupère les informations spécifiques au type de la source (pdl, pce, cpcu, regaz, elecsavoie)
 */
const getInformationByType = computed(() => {
    return sourceStore.getInformationByType(props.meterId)
})

/**
 * Identifiant bâtiment courant (page compteur) pour {@link SourceExtendDialog}.
 */
const getExtendBuildingId = computed(() => buildingStore.building?._id ?? "")

/**
 * Titulaire du consentement pour le prolongement : même entreprise (`_id_company`)
 * ou même personne (`_id_user` / e-mail `username`).
 */
const getExtendConsentHolder = computed(() => {
    const tit = getInformationByType.value?.consentement?.titulaire
    return isCurrentUserHolderForExtend(titulaireToExtendParty(tit), {
        userId: authStore.userId,
        username: authStore.username,
        companyId: authStore.companyId,
    })
})

/**
 * Données compteur + titulaire pour le prolongement via {@link SourceExtendDialog}.
 */
const getExtendMeter = computed((): SourceExtendDialogMeter | null => {
    const source = getSourceById.value
    if (!source) return null
    const tit = getInformationByType.value?.consentement?.titulaire
    if (!tit?.user) return null
    return {
        _id: props.meterId,
        idtype: String(source.type),
        idsource: String(source.idsource),
        address: plainAddressForExtend(source.address),
        owner: {
            _id: tit.user._id_user ?? undefined,
            firstname: tit.user.firstname ?? "",
            lastname: tit.user.lastname ?? "",
            username: tit.user.username ?? "",
            company: tit.company?.name ?? null,
            companyType: tit.company?.type ?? null,
        },
    }
})

/**
 * Récupère le badge correspondant à l'état du consentement
 */
const getConsentBadge = computed<LineBlockBadge>(() => {
    const consent = getInformationByType.value?.consentement

    const statusMap: Record<ConsentementEtat, LineBlockBadge> = {
        revoked: {
            status: "warn",
            icon: "pi pi-ban",
            text: t("state_consent.revoked"),
        },
        outdated: {
            status: "warn",
            icon: "pi pi-ban",
            text: t("state_consent.outdated"),
        },
        unsigned: {
            status: "warn",
            icon: "pi pi-file-edit",
            text: t("state_consent.unsigned"),
        },
        unknown: {
            status: "warn",
            icon: "pi pi-file-edit",
            text: t("state_consent.unknown"),
        },
        active: {
            status: "success",
            icon: "pi pi-check-circle",
            text: t("state_consent.active"),
        },
    }

    if (!consent?.etat)
        return {
            status: "warn",
            icon: "pi pi-exclamation-triangle",
            text: t("state_consent.error"),
        }

    return consent
        ? (statusMap[consent?.etat] ?? {
              status: "warn",
              icon: "pi pi-exclamation-triangle",
              text: t("state_consent.error"),
          })
        : {
              status: "warn",
              icon: "pi pi-exclamation-triangle",
              text: t("state_consent.error"),
          }
})

/**
 * Récupère le nom formaté du propriétaire de la source.
 * Si une entreprise est associée, affiche "Entreprise (représentée par Prénom Nom)"
 * Sinon affiche "Prénom Nom" ou "Non disponible" si les deux sont absents.
 */
const getOwner = computed(() => {
    if (!getInformationByType.value?.consentement?.titulaire) {
        return t("owner.unknown")
    }

    const fullName = formatPerson(
        getInformationByType.value?.consentement.titulaire.user.firstname || "",
        getInformationByType.value?.consentement.titulaire.user.lastname || ""
    )

    if (
        getInformationByType.value?.consentement.titulaire.company?._id_company
    ) {
        return `${getInformationByType.value?.consentement?.titulaire.company.name} (${t("owner.on_behalf")} ${fullName})`
    }

    return fullName || t("owner.unknown")
})

/**
 * Indique si l'utilisateur connecté est le propriétaire du consentement
 */
const isOwner = computed(() => {
    const consent = getInformationByType.value?.consentement
    // si le signataire est une personne physique alors on vérifie que c'est bien la même personne qui a signé le consentement
    if (authStore?.userId === consent?.titulaire?.user?._id_user) {
        return true
    }
    // si le signataire est une personne morale alors on a deux possibilités
    // 1 - l'utilisateur connecté est admin et appartient à la même entreprise qui a signé le consentement
    // 2 - l'utilisateur connecté est le signataire du consentement
    return (
        (authStore.companyId === consent?.titulaire?.company?._id_company &&
            authStore.isCompanyAdmin) ||
        authStore.userId === consent?.titulaire?.user?._id_user
    )
})

/**
 * Indique si le composant MeterCardMessageError doit être affiché
 * Conditions :
 * 1 - La source doit être de type PCE
 * 2 - Le statut du droit d'accès ne doit pas être "ACTIVE" ou "RECHECK"
 */
const isDisplayGrdfError = computed(() => {
    return (
        getSourceById.value?.type === SOURCES_TYPES.PCE &&
        ![PCE_DDA_STATUS.ACTIVE, PCE_DDA_STATUS.RECHECK].includes(
            (getInformationByType.value as InformationPCE).droit_access?.etat
        )
    )
})

/**
 * Récupère le menu des actions possibles sur le consentement Homeys
 *
 * Actions possibles :
 * 1 - Télécharger le consentement
 * 2 - Prolonger le consentement
 * 3 - Révoquer le consentement
 *
 * Des conditions s'appliquent pour afficher certaines actions :
 * - L'action "Révoquer" n'est disponible que si le consentement n'est pas déjà révoqué,
 *   si l'utilisateur est le propriétaire du consentement, et si l'utilisateur est un client Homeys avec
 *   un droit d'accès employé ou plus, ou un UC2 particulier ou pro.
 * - L'action "Changer de titulaire" est disponible pour les clients Homeys avec un droit d'accès employé ou plus,
 *   et dépend du type de source (PDL ou PCE) avec des conditions spécifiques. Pour les PCE, le droit d'accès doit être inactif.
 * - L'action "Prolonger" est toujours disponible pour le propriétaire du consentement, sinon elle est présentée comme "Demander le prolongement".
 */
const getConsentMenu = computed(() => {
    let tabs: { label: string; command: () => void | Promise<void> }[] = []

    if (
        ![SOURCES_TYPES.PDL, SOURCES_TYPES.PCE].includes(
            getSourceById.value?.type ?? ""
        )
    ) {
        return tabs
    }

    tabs = [
        {
            label: t("options.download"),
            command: async () => {
                let notification: ToastMessageOptions | null = null
                busTask.emit({
                    severity: "info",
                    title: "Téléchargement en cours",
                    message:
                        "Le consentement est en cours de génération, veuillez patienter...",
                    callback,
                })
                const data = await $getPreviewConsent(
                    getInformationByType.value?.consentement
                        ?._id_consentement || ""
                )

                const promise = $getPreviewConsent(
                    getInformationByType.value?.consentement
                        ?._id_consentement || ""
                )

                function callback(_notification: ToastMessageOptions) {
                    notification = _notification
                }

                if (promise) {
                    await promise
                    downloadPDF(data.base64Data, "consentement")
                    if (notification) toast.remove(notification)
                }
            },
        },
        {
            label: t("options.extend", { isOwner: isOwner.value }),
            command: () => {
                if (getExtendMeter.value) {
                    modalExtendConsentVisible.value = true
                }
            },
        },
    ]

    const dismissed =
        getInformationByType.value?.consentement?.etat === "revoked"

    // il peut révoqué le consentement si pas déjà révoqué + si l'utilisateur est
    // 1 - propriétaire de la source + client homeys avec un droit > employé
    // 2 - propriétaie de la source + particulier UC2 ou pro UC2
    const isClientHomeys =
        authStore.isClientHomeys && authStore.isEmployeWriteOrHigher

    // Si l'utilisateur est un UC2 particulier ou pro
    const isUC2 = authStore.isUc2Particulier || authStore.isUc2Pro

    // conditions pour afficher le bouton révoquer
    const canRevoke = !dismissed && isOwner.value && (isClientHomeys || isUC2)

    // Si on peut révoquer alors on ajoute l'option
    if (canRevoke) {
        tabs.push({
            label: t("options.revoke"),
            command: () => {
                modalMeterRevokeConsentToggle.value = true
            },
        })
    }

    // si l'utilisateur est un UC1 alors il peut
    // changer le titulaire de la source par demande.
    // On vérifie aussi si le droit d'accès de la source est actif,
    // car seulement les DDA inactive sont autorisés à changer de titulaire
    let canChangeOwner = false

    // En fonction du type de source on applique les règles
    switch (getSourceById.value?.type) {
        // Si on est sur un PDL alors on vérifie que l'utilisateur est un client homeys avec un droit employé ou plus
        case SOURCES_TYPES.PDL:
            canChangeOwner = isClientHomeys && authStore.isEmployeWriteOrHigher
            break
        // Si on est sur un PCE alors on vérifie que l'utilisateur est un client homeys avec un droit employé ou plus et que la déclaration de droit d'accès n'est pas active
        case SOURCES_TYPES.PCE: {
            const droitAccess =
                (getInformationByType.value as InformationPCE)?.droit_access
                    ?.etat || DDA_STATUS.EMPTY

            const isDDAActive = [
                DDA_STATUS.ACTIVE,
                DDA_STATUS.A_VALIDER,
                DDA_STATUS.RECHECK,
            ].includes(droitAccess)
            canChangeOwner =
                isClientHomeys &&
                !isDDAActive &&
                authStore.isEmployeWriteOrHigher
            break
        }
    }

    // Ajoute   l'option changer de titulaire si l'utilisateur le peut
    if (canChangeOwner) {
        tabs.push({
            label: t("options.change_owner"),
            command: () => {
                if (getExtendMeter.value) {
                    modalChangeOwnerVisible.value = true
                }
            },
        })
    }
    return tabs
})

/**
 * construit les lineblocks pour chaque type de source
 */
const getConsentByType = computed(() => {
    const source = getSourceById.value
    if (!source) return { lineBlocks: [] as LineBlock[], menu: {} }

    const lineBlocks: LineBlock[] = []

    // pour un PDL, PCE ou CPCU on affiche les données du consentement
    switch (source.type) {
        case SOURCES_TYPES.PDL:
            lineBlocks.push(
                generateConsentHomeysLineBlock(t("homeys.title"), {
                    menu: getConsentMenu.value,
                })
            )
            break

        case SOURCES_TYPES.PCE: {
            const droitAccess = source.informations?.pce?.droit_access

            if (droitAccess) {
                const accesGrdf: LineBlock = {
                    title: [],
                    badge: [],
                    lines: [],
                    id: "grdf",
                }
                accesGrdf.title = [t("pce.grdf_access")]
                accesGrdf.badge = [
                    {
                        text: t("pce.right_acess", {
                            etat_droit_acces: droitAccess?.etat,
                        }),
                        icon:
                            droitAccess?.etat === DDA_STATUS.ACTIVE
                                ? "pi pi-check-circle"
                                : "pi pi-exclamation-triangle",
                        status:
                            droitAccess?.etat === DDA_STATUS.ACTIVE
                                ? "success"
                                : "warn",
                    },
                ]
                accesGrdf.lines = [
                    {
                        text: t("pce.date_begin_access"),
                        value: t("date", {
                            date: droitAccess?.debut_droit_acces || null,
                        }),
                    },
                    {
                        text: t("pce.date_end_access"),
                        value: t("date", {
                            date: droitAccess?.fin_droit_acces,
                        }),
                    },
                ]
                lineBlocks.push(accesGrdf)
            }
            lineBlocks.push(
                generateConsentHomeysLineBlock(t("homeys.title"), {
                    menu: getConsentMenu.value,
                })
            )
            break
        }
        case SOURCES_TYPES.CPCU:
            // Consentement API
            lineBlocks.push(
                generateConsentHomeysLineBlock(t("cpcu.api_access"))
            )
            // Consentement espace client
            lineBlocks.push(
                generateConsentHomeysLineBlock(t("cpcu.billing_access"), {
                    menu: getConsentMenu.value,
                })
            )
            break
        case SOURCES_TYPES.REGAZ:
            // Consentement API
            lineBlocks.push(
                generateConsentHomeysLineBlock(t("regaz.api_access"))
            )
            break
        case SOURCES_TYPES.ELECSAVOIE:
            lineBlocks.push(
                generateConsentHomeysLineBlock(t("elecsavoie.api_access"), {
                    menu: getConsentMenu.value,
                })
            )
            break
        case SOURCES_TYPES.ELECSTRASBOURG:
            lineBlocks.push(
                generateConsentHomeysLineBlock(t("elecstrasbourg.api_access"), {
                    menu: getConsentMenu.value,
                })
            )
            break
        case SOURCES_TYPES.DALKIA:
            // Consentement API
            lineBlocks.push(
                generateConsentHomeysLineBlock(t("dalkia.api_access"))
            )
            break
        case SOURCES_TYPES.SIGDU:
            // Consentement API
            lineBlocks.push(
                generateConsentHomeysLineBlock(t("sigdu.api_access"))
            )
            break
        case SOURCES_TYPES.UBORDEAUX:
            // Aucun mandat : l'Université dépose elle-même les exports (DEV-2100).
            // dapi sert `etat: null`, que le bloc Homeys lirait « Erreur inconnue ».
            lineBlocks.push(buildUBordeauxConsentBlock(t))
            break
    }

    return { lineBlocks, menu: {} }
})

// ================================
// BUSINESS LOGIC
// ================================

/**
 * Construit un line block pour afficher les informations du consentement Homeys
 * @param title - le titre du line block
 * @param menu - les commandes du menu (optionnel)
 */
function generateConsentHomeysLineBlock(
    title: string,
    {
        menu = [],
    }: {
        menu?: LineBlockCommand[]
    } = {
        menu: [],
    }
): LineBlock {
    const block = {
        title: [title],
        badge: [getConsentBadge.value],
        lines: [],
        menu,
    } as LineBlock

    const lines = []
    if (getInformationByType.value?.consentement) {
        lines.push(
            ...[
                {
                    text: t("homeys.date_begin"),
                    value: t("date", {
                        date: getInformationByType.value?.consentement
                            .date_from,
                    }),
                },
                {
                    text: t("homeys.date_end"),
                    value: t("date", {
                        date: getInformationByType.value?.consentement.date_to,
                    }),
                },
            ]
        )
    }

    if (getOwner.value) {
        lines.push({
            text: t("owner.label"),
            value: getOwner.value,
        })
    }

    block.lines.push(...lines)

    return block
}

// ================================
// INTERACTIONS
// ================================

/**
 * Gère l'action à effectuer lors du clic sur le bouton renouveler l'accès
 */
function onRenewConsent() {
    if (getExtendMeter.value) {
        modalExtendConsentVisible.value = true
    }
}

/**
 * Recharge la source après prolongement signé ou invitation ; pas d’appel si simple abandon (quit).
 */
async function onExtendDialogDone(
    payload: SourceExtendDialogDonePayload
): Promise<void> {
    if (payload.kind === "quit") {
        return
    }
    await sourceStore.fetchSourceById({
        _id_source: props.meterId,
    })
}

async function onChangeOwnerDialogDone(): Promise<void> {
    await sourceStore.fetchSourceById({
        _id_source: props.meterId,
    })
}
</script>

<template>
    <div class="meter-card-consent flex flex-col mt-6 gap-6">
        <!-- On affiche les détails du consentement -->
        <div
            v-for="(lineBlocks, index) in getConsentByType.lineBlocks"
            :key="index"
            class=""
        >
            <MeterCardLinesBlocks :line-blocks="lineBlocks">
                <!-- slot pour personnaliser le contenu de la ligne -->
                <template #footer>
                    <MeterGrdfMessageError
                        class="mt-4"
                        v-if="isDisplayGrdfError && lineBlocks.id === 'grdf'"
                        :meter-id="meterId"
                    />
                </template>
            </MeterCardLinesBlocks>
        </div>

        <!-- Modale pour révoquer le consentement -->
        <DialogMeterRevokeConsent
            v-model:visible="modalMeterRevokeConsentToggle"
            :consent-id="getInformationByType?.consentement?._id_consentement"
            :idsource="getSourceById?.idsource"
            @update="
                sourceStore.fetchSourceById({
                    _id_source: meterId,
                })
            "
        />
        <SourceExtendDialog
            v-if="getExtendMeter"
            v-model:visible="modalExtendConsentVisible"
            :meter="getExtendMeter"
            :building-id="getExtendBuildingId"
            :is-consent-holder="getExtendConsentHolder"
            @done="onExtendDialogDone"
        />
        <SourceChangeDialog
            v-if="getExtendMeter"
            v-model:visible="modalChangeOwnerVisible"
            :meter="getExtendMeter"
            :building-id="getExtendBuildingId"
            @done="onChangeOwnerDialogDone"
        />
        <BaseMessage
            class="flex flex-row items-center"
            severity="warn"
            icon="pi pi-exclamation-triangle"
            v-if="
                getInformationByType?.consentement.etat === 'revoked' &&
                (isOwner || authStore.isClientHomeys)
            "
        >
            <div class="flex flex-col gap-2">
                <p class="font-semibold">{{ t("warning.title_revoked") }}</p>
                <p class="text-sm">{{ t("warning.content_revoked") }}</p>
                <BaseButton severity="warn max-w-80" @click="onRenewConsent">{{
                    t("warning.button_renew")
                }}</BaseButton>
            </div>
        </BaseMessage>
    </div>
</template>
